<!--
  Purpose:     Manage calculation properties for field aggregation within the current table.
  Component:   Field Calculation Modal: Aggregation panel.
  Path:        Field - Calculation - Aggregation.
  Calculation:
  - aggregationFieldGuid  Field whose values will be aggregated. [String]
  - aggregateFunction     Function to apply to Aggregation Field. [String]
  - filterFormula         Javascript expression to filter rows in the Aggregation (current) Table. [String]
  Helpers:
  - Formula Editor        Code entry control to edit Filter Formula.
  - Table Fields          List of Fields in Aggregation (current) Table that can be embedded in Filter Formula. [Listbox]
  - Row Fields            List of Fields in Target (current) Table Row that can be embedded in Filter Formula (with 'ROW:' prefix). [Listbox]
  - Atlas Assistant       AI Chat service to help generate filter formula.
-->
<template>
  <div>
    <!-- First row - Aggregation Field and Aggregation Function. -->
    <div class="row">
      <div class="col-6">
        <b-form-group label="Field to Aggregate">
          <b-form-select v-model="calculationData.aggregationFieldGuid" :options="aggregationFieldOptions" />
        </b-form-group>
      </div>
      <div class="col-6">
        <b-form-group label="Aggregate Function">
          <aggregate-function-select :aggregation-field="aggregationField"
            v-model="calculationData.aggregateFunction" />
        </b-form-group>
      </div>
    </div>
    <div class="row">
      <div class="col-4">
        <!-- Table Field selection. -->
        <b-form-group class="properties_group field-selector" label="Table Fields">
          <field-selector ref="tableFieldSelector" :table="table" :fields-to-ignore="selectorTableFieldsToIgnore"
            @add-field="addTableFieldToFormula" />
        </b-form-group>
        <!-- Row Field selection. -->
        <b-form-group class="properties_group field-selector" label="Row Fields">
          <field-selector ref="rowFieldSelector" :table="table" :fields-to-ignore="selectorRowFieldsToIgnore"
            @add-field="addRowFieldToFormula" />
        </b-form-group>
      </div>
      <div class="col-8">

        <!-- Formula Editor -->
        <calculation-formula-code-editor id="formulaEditor" ref="formulaEditor" label="Filter Formula"
          language="javascript" :formula="filterFormula" :constructFieldDefinition="constructFieldDefinition"
          @formula-changed="onFormulaChanged" :calculation-warning-on-verify="calculationWarningOnVerify" :atlas-assistant="atlasAssistant" />

        <!-- Atlas Assistant -->
        <atlas-assistant ref="atlasAssistant" class="atlasAssistant mt-3 mb-1" query-type="AggregationFilter"
          :target-field="tableField" :query-fields="assistantQueryFields"
          :additionalFunctions="assistantAdditionalFunctions" @use-assistant-formula="useAssistantFormula"
          :log-assistant-conversation="false" />

      </div>
    </div>
  </div>
</template>

<script>

import FieldSelector from '../../components/FieldSelector.vue';
import AggregateFunctionSelect from './AggregateFunctionSelect.vue';
import CalculationFormulaCodeEditor from './CalculationFormulaCodeEditor.vue';
import AtlasAssistant from '../../components/AtlasAssistant.vue';

export default {

  name: 'AggregationPanel',

  components: {
    FieldSelector,
    AggregateFunctionSelect,
    CalculationFormulaCodeEditor,
    AtlasAssistant,
  },

  inject: [
    // Current Table. [from CalculationModel].
    'table',
    // Current Field. [from CalculationModel].
    'tableField',
    // Calculation data - to be populated with Lookup Pairs. [from CalculationModel].
    'calculationData',
    /** Clone field with current Calculation properties. [from CalculationModel] */
    'constructFieldDefinition',
    // Utility functions etc. [from CalculationModel].
    'apiUtils',
    'entityUtils',
  ],

  props: {},

  data() {
    return {
      /** Filter Formula as presented in the Editor. */
      filterFormula: '',
    };
  },

  watch: {

    'calculationData.aggregationFieldGuid'(vewVal){
      this.clearformulaVerify();
    },

    'calculationData.aggregateFunction'(vewVal){
      this.clearformulaVerify();
    },

  },

  computed: {

    // Fields available for selection as Aggregation Field.
    aggregationFieldOptions() {
      return this.table.fields
        .filter(fld =>
          // Ignore current Field.
          fld.fieldGuid !== this.tableField.fieldGuid &&
          // Field type must match current field.
          this.entityUtils.getFieldDataType(fld) == this.entityUtils.getFieldDataType(this.tableField) &&
          // Ignore RLS/Internal fields.
          !fld.isRLSField && !fld.isInternal
        ).map((fld) => {
          // Convert to Select Option: { value: guid, text: name }.
          return { value: fld.fieldGuid, text: fld.displayName + ' (' + this.entityUtils.getFieldDataTypeName(fld).toLowerCase() + ')' }
        });
    },

    /** Field to Aggregate - restricts available functions. */
    aggregationField() {
      return this.table.fields.find(fld => fld.fieldGuid === this.calculationData.aggregationFieldGuid);
    },

    /** Table Fields to exclude from selector. */
    selectorTableFieldsToIgnore() {
      return this.table.fields.filter(fld => fld.fieldGuid == this.tableField.fieldGuid || fld.isRLSField || fld.isInternal);
    },

    /** Row Fields to exclude from selector. */
    selectorRowFieldsToIgnore() {
      return this.table.fields.filter(fld => fld.fieldGuid == this.tableField.fieldGuid || fld.isRLSField || fld.isInternal);
    },

    // Fields available for Assistant to include in query.
    assistantQueryFields() {
      return this.table.fields
        // Ignore current Field.
        .filter((fld) => { return fld.fieldGuid !== this.tableField.fieldGuid && !fld.isRLSField; });
    },

    /** Additional (bespoke, non-standard) functions available for the Assistant to use in the formula. */
    assistantAdditionalFunctions() {
      // May want to include functions in the query.
      return [] // this.functionsToRender
        .filter(fn => fn.bespoke && (fn.variants || []).length)
        .map(fn => `${fn.name}(${(fn.variants[0].params || []).map(prm => prm.name).join(', ')}) [${fn.purpose}].`);
    },

  },

  methods: {

    // Formula Editor utilities.
    getformulaEditor() {
      return this.$refs.formulaEditor;
    },
    onFormulaChanged(formula) {
      this.filterFormula = formula;
      this.calculationData.filterFormula = this.entityUtils.changeFieldNamesToGuids(formula, this.table.fields);
    },
    replaceEditorTextSelection(text, cursorOffset = 0) {
      this.getformulaEditor().replaceTextSelection(text, cursorOffset);
    },
    clearformulaVerify() {
      return this.getformulaEditor().setFormulaVerify();
    },

    /** 
     * Basic validation on the Calculation to determine whether the formula can be verified.
     * - Returns a warning message to display below the formula editor in place of invoking the Verify logic.
     * - Called by Code Editor prior to verifying.
     */
    calculationWarningOnVerify(){
      if (!this.calculationData.aggregationFieldGuid) return 'Aggregation Field must be selected';
      if (!this.calculationData.aggregateFunction) return 'Aggregate Function must be selected';
    },

    // Table Field selection.
    addTableFieldToFormula(field) {
      if (!field) return;
      this.replaceEditorTextSelection(`{{'${field.displayName}'}}`);
    },

    // Row Field selection.
    addRowFieldToFormula(field) {
      if (!field) return;
      this.replaceEditorTextSelection(`{{'ROW:${field.displayName}'}}`);
    },

    // Atlas Assistant.
    atlasAssistant() {
      return this.$refs.atlasAssistant;
    },
    askAssistantError(error) {
      this.atlasAssistant().askFormulaError(this.filterFormula, error);
      this.setFormulaVerify();
    },
    useAssistantFormula(formula) {
      this.filterFormula = this.entityUtils.changeFieldGuidsToNames(formula, this.table.fields);
    },

  },

  async beforeMount() {
    // Ensure that Calculation data is initialised.
    if (!this.calculationData.hasOwnProperty('aggregationFieldGuid'))
      this.$set(this.calculationData, 'aggregationFieldGuid', '');
    if (!this.calculationData.hasOwnProperty('aggregateFunction'))
      this.$set(this.calculationData, 'aggregateFunction', '');
    if (!this.calculationData.hasOwnProperty('filterFormula'))
      this.$set(this.calculationData, 'filterFormula', '');

    // Populate formula to edit with field names.
    this.filterFormula = this.entityUtils.changeFieldGuidsToNames(this.calculationData.filterFormula, this.table.fields);
  },

};

</script>

<style scoped lang="scss">
#formulaEditor {
  ::v-deep #codeEditor {
    height: 260px;
    max-height: 320px;
  }
}

.properties_group.field-selector {
  max-height: 320px;
}
</style>